import { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";

/** The server's report (server live/recording-fields.ts). */
interface Report {
  source: string;
  files: number;
  sessionId: number | null;
  sessions: { sessionId: number; states: number }[];
  states: number;
  carRecords: number;
  fields: { key: number; name: string; source: string; records: number; cars: number }[];
  loopNames: { name: string; count: number }[];
  sectionIds: { id: string; count: number }[];
  sectionSamples: unknown[];
  cars: { car: string; records: number; gps: number; lapPct: number; loop: number; sections: number; signalBars: [number, number] | null; gpsHealth: [number, number] | null }[];
  pushes: { messages: { name: string; count: number }[]; patchFields: { field: string; count: number }[] };
}
interface Job {
  status: "running" | "done" | "failed";
  report: Report | null;
  error: string | null;
}

const SOURCE_LABEL: Record<string, string> = { gps: "GPS", loops: "Timing loops", timing: "Timing estimate" };
const pct = (n: number, of: number) => (of ? `${Math.round((100 * n) / of)}%` : "–");

/**
 * Admin, Live replays: which car-position fields a replay's stored recording holds (GPS, the
 * circuit's timing loops, the pit loops), checked on the server in the background.
 */
export function RecordingFields({ slug }: { slug: string }) {
  const [job, setJob] = useState<Job | null>(null);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => void (timer.current && clearTimeout(timer.current)), []);

  const ask = (again = false) => {
    setError(null);
    api
      .get<Job>(`/admin/live-replays/${encodeURIComponent(slug)}/fields${again ? "?again=1" : ""}`)
      .then((j) => {
        setJob(j);
        if (j.status === "running") timer.current = setTimeout(() => ask(), 3000);
      })
      .catch((e: Error) => setError(e.message || "Could not check the recording"));
  };

  if (!job && !error) {
    return (
      <button type="button" onClick={() => ask()} className="px-3 py-1.5 border border-gray-300 dark:border-gray-700 rounded-lg text-xs font-medium hover:bg-gray-50 dark:hover:bg-gray-900">
        Check recording fields
      </button>
    );
  }
  if (error) return <p className="w-full text-xs text-red-600 dark:text-red-400">{error}</p>;
  if (job!.status === "running") return <p className="w-full text-xs text-gray-500 dark:text-gray-400">Checking the recording… (a long race takes a minute or two)</p>;
  if (job!.status === "failed") return <p className="w-full text-xs text-red-600 dark:text-red-400">The check failed: {job!.error}</p>;
  const r = job!.report!;
  const th = "px-2 py-1 text-left font-semibold";
  const td = "px-2 py-1";
  return (
    <div className="w-full space-y-3 pt-2 text-xs text-gray-700 dark:text-gray-300">
      <p className="text-gray-500 dark:text-gray-400">
        {r.files} recording files · session {r.sessionId ?? "–"} · {r.states} states · {r.carRecords} car records ·{" "}
        <button type="button" className="underline" onClick={() => ask(true)}>
          check again
        </button>
      </p>
      <table className="w-full border-collapse">
        <thead className="text-gray-500 dark:text-gray-400">
          <tr>
            <th className={th}>Field</th>
            <th className={th}>Source</th>
            <th className={`${th} text-right`}>Car records with it</th>
            <th className={`${th} text-right`}>Cars with it</th>
          </tr>
        </thead>
        <tbody>
          {r.fields.map((f) => (
            <tr key={f.key} className="border-t border-gray-200 dark:border-gray-800">
              <td className={td}>
                {f.name} <span className="text-gray-400">({f.key})</span>
              </td>
              <td className={td}>{SOURCE_LABEL[f.source] ?? f.source}</td>
              <td className={`${td} text-right tabular-nums`} style={{ color: f.records ? undefined : "#ef4444" }}>
                {f.records} ({pct(f.records, r.carRecords)})
              </td>
              <td className={`${td} text-right tabular-nums`}>
                {f.cars} of {r.cars.length}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p>
        <b>Loop names:</b> {r.loopNames.length ? r.loopNames.map((l) => `${l.name} (${l.count})`).join(", ") : "none"}
      </p>
      <p>
        <b>Section ids:</b> {r.sectionIds.length ? r.sectionIds.map((s) => `${s.id} (${s.count})`).join(", ") : "none"}
      </p>
      {r.sectionSamples.length > 0 && <pre className="overflow-x-auto rounded bg-gray-100 p-2 dark:bg-gray-900">{JSON.stringify(r.sectionSamples, null, 1)}</pre>}
      <p>
        <b>Pushes:</b> {r.pushes.messages.map((m) => `${m.name} (${m.count})`).join(", ") || "none"}
        {r.pushes.patchFields.length > 0 && (
          <>
            <br />
            <b>Fields the car patches set:</b> {r.pushes.patchFields.map((f) => `${f.field} (${f.count})`).join(", ")}
          </>
        )}
      </p>
      <details>
        <summary className="cursor-pointer font-semibold">By car</summary>
        <table className="mt-1 w-full border-collapse">
          <thead className="text-gray-500 dark:text-gray-400">
            <tr>
              <th className={th}>Car</th>
              <th className={`${th} text-right`}>Records</th>
              <th className={`${th} text-right`}>GPS</th>
              <th className={`${th} text-right`}>Lap %</th>
              <th className={`${th} text-right`}>Loop</th>
              <th className={`${th} text-right`}>Sections</th>
              <th className={`${th} text-right`}>Signal bars</th>
              <th className={`${th} text-right`}>GPS health</th>
            </tr>
          </thead>
          <tbody>
            {r.cars.map((c) => (
              <tr key={c.car} className="border-t border-gray-200 tabular-nums dark:border-gray-800">
                <td className={td}>#{c.car}</td>
                <td className={`${td} text-right`}>{c.records}</td>
                <td className={`${td} text-right`}>{pct(c.gps, c.records)}</td>
                <td className={`${td} text-right`}>{pct(c.lapPct, c.records)}</td>
                <td className={`${td} text-right`}>{pct(c.loop, c.records)}</td>
                <td className={`${td} text-right`}>{pct(c.sections, c.records)}</td>
                <td className={`${td} text-right`}>{c.signalBars ? c.signalBars.join("–") : "–"}</td>
                <td className={`${td} text-right`}>{c.gpsHealth ? c.gpsHealth.join("–") : "–"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  );
}
